import React, { useState, useEffect, useCallback } from 'react'
import { format } from 'date-fns'
import { Clock, RotateCcw, FileText, User, ChevronDown, ChevronUp, Save, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiService } from '@/services/api'
import { toast } from 'sonner'

interface Revision {
  id: string
  blog_post_id: string
  version: number
  title: string
  content: string
  excerpt?: string
  status?: string
  revision_notes?: string
  created_by?: string
  created_at: string
  is_autosave: boolean
}

interface BlogPostRevisionsProps {
  postId: string
  currentVersion?: number
  onRestore?: () => void
}

export const BlogPostRevisions: React.FC<BlogPostRevisionsProps> = ({
  postId,
  currentVersion = 1,
  onRestore,
}) => {
  const [revisions, setRevisions] = useState<Revision[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRevision, setSelectedRevision] = useState<Revision | null>(null)
  const [showRestoreDialog, setShowRestoreDialog] = useState(false)
  const [expandedRevision, setExpandedRevision] = useState<string | null>(null)
  const [restoring, setRestoring] = useState(false)

  const fetchRevisions = useCallback(async () => {
    try {
      setLoading(true)
      const data = await apiService.blog.revisions.list(postId)
      setRevisions(data)
    } catch (error) {
      console.error('Failed to fetch revisions:', error)
      toast.error('Failed to load revision history')
    } finally {
      setLoading(false)
    }
  }, [postId])

  useEffect(() => {
    fetchRevisions()
  }, [fetchRevisions])

  const handleRestore = async () => {
    if (!selectedRevision) return

    try {
      setRestoring(true)
      await apiService.blog.revisions.restore(postId, selectedRevision.id)
      toast.success(`Restored to revision ${selectedRevision.version}`)
      setShowRestoreDialog(false)
      fetchRevisions()
      onRestore?.()
    } catch (error) {
      console.error('Failed to restore revision:', error)
      toast.error('Failed to restore revision')
    } finally {
      setRestoring(false)
    }
  }

  const handleCreateManualRevision = async () => {
    try {
      await apiService.blog.revisions.create(postId, {
        revision_notes: 'Manual checkpoint created',
        is_autosave: false,
      })
      toast.success('Revision created successfully')
      fetchRevisions()
    } catch (error) {
      console.error('Failed to create revision:', error)
      toast.error('Failed to create revision')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (revisions.length === 0) {
    return (
      <div className="text-center p-8">
        <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground mb-4">No revision history yet</p>
        <Button onClick={handleCreateManualRevision} size="sm">
          <Save className="w-4 h-4 mr-2" />
          Create First Revision
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Revision History</h3>
        <Button onClick={handleCreateManualRevision} size="sm" variant="outline">
          <Save className="w-4 h-4 mr-2" />
          Save Checkpoint
        </Button>
      </div>

      <div className="space-y-2">
        {revisions.map((revision) => {
          const isCurrent = revision.version === currentVersion
          const isExpanded = expandedRevision === revision.id

          return (
            <Card key={revision.id} className={isCurrent ? 'ring-2 ring-primary' : ''}>
              <Collapsible
                open={isExpanded}
                onOpenChange={(open) => setExpandedRevision(open ? revision.id : null)}
              >
                <CollapsibleTrigger className="w-full">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={isCurrent ? 'default' : 'secondary'}>
                            v{revision.version}
                          </Badge>
                          {revision.is_autosave && (
                            <Badge variant="outline">Auto-save</Badge>
                          )}
                          {isCurrent && (
                            <Badge variant="default" className="bg-blue-500">
                              Current
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-medium text-sm mb-1">{revision.title}</h4>
                        {revision.revision_notes && (
                          <p className="text-xs text-muted-foreground italic mb-1">
                            {revision.revision_notes}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(revision.created_at), 'MMM d, yyyy h:mm a')}
                          </span>
                          {revision.created_by && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              User
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isCurrent && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedRevision(revision)
                              setShowRestoreDialog(true)
                            }}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-4 pb-4 border-t border-border">
                    <div className="mt-3 space-y-2">
                      <div>
                        <span className="text-xs font-medium text-muted-foreground">Status:</span>
                        <span className="ml-2 text-xs">{revision.status || 'draft'}</span>
                      </div>
                      {revision.excerpt && (
                        <div>
                          <span className="text-xs font-medium text-muted-foreground">Excerpt:</span>
                          <p className="text-xs mt-1 text-muted-foreground">{revision.excerpt}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-xs font-medium text-muted-foreground">Content Preview:</span>
                        <p className="text-xs mt-1 text-muted-foreground line-clamp-3">
                          {revision.content.replace(/<[^>]*>/g, '').slice(0, 200)}...
                        </p>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )
        })}
      </div>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore Revision</DialogTitle>
            <DialogDescription>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                  <AlertCircle className="w-4 h-4" />
                  <span>This action will create a backup of the current version before restoring.</span>
                </div>
                {selectedRevision && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm font-medium mb-1">
                      Restoring to Version {selectedRevision.version}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Created on {format(new Date(selectedRevision.created_at), 'MMM d, yyyy h:mm a')}
                    </p>
                    {selectedRevision.revision_notes && (
                      <p className="text-xs text-muted-foreground mt-1 italic">
                        Note: {selectedRevision.revision_notes}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRestoreDialog(false)}
              disabled={restoring}
            >
              Cancel
            </Button>
            <Button onClick={handleRestore} disabled={restoring}>
              {restoring ? 'Restoring...' : 'Restore Version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}